import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";
import { useUserId } from "@/hooks/useUserId";
import { useRoomState } from "@/hooks/useRoomState";
import { formatSecondsAsDuration } from "@/lib/duration";
import type { RoomState } from "ghost-server";

type RoomSummary = {
	id: string;
	name: string;
	createdAt: Date;
	ghostedAt: Date | null;
	lastMessageAt: Date | null;
	isVIPRoom: boolean;
	userCount?: number;
	stateOverrides?: { state: string; transitionAfterSeconds: number }[];
};

function RoomRow({ room, states }: { room: RoomSummary; states: RoomState[] }) {
	const navigate = useNavigate();
	const roomState = useRoomState(room, states);
	const stateKey = roomState?.current?.state ?? "";
	const isGhosted = stateKey === "ghost" || room.ghostedAt != null;
	const shouldShowStatus =
		stateKey === "ghost" ||
		(!!roomState &&
			roomState.current?.state !== "default" &&
			roomState.elapsedSeconds != null &&
			roomState.current !== null &&
			roomState.elapsedSeconds >= roomState.current.transitionAfterSeconds);
	const shouldShowAlive = !isGhosted;
	const stateObj = states.find((s) => s.state === stateKey);
	const isDefaultState = !stateKey || stateKey === "default";
	const roomNameColor = isGhosted ? "#7A7A7A" : shouldShowStatus && stateObj?.color ? stateObj.color : "#DCDCDC";
	const activeColor = shouldShowStatus && stateObj?.color ? stateObj.color : "#DCDCDC";

	return (
		<button
			onClick={() => navigate(`/room/${room.id}`)}
			className="w-full flex flex-col min-[1000px]:flex-row min-[1000px]:items-center min-[1000px]:justify-between gap-3 min-[1000px]:gap-0 px-4 py-4 min-[1000px]:py-5 border-b border-dashed border-white/40 text-left transition-colors hover:bg-white/5"
		>
			<div className="w-full min-[1000px]:w-auto min-[1000px]:max-w-70 min-w-0 flex items-center gap-2">
				<p
					className="block max-w-full font-tomorrow-light text-[16px] leading-[130%] tracking-[20%] truncate"
					style={{ color: roomNameColor }}
				>
					{room.isVIPRoom && <span className="text-[#ffcc00] mr-2">★</span>}
					{room.name}
				</p>
			</div>
			<div className="w-full min-[1000px]:w-auto min-[1000px]:ml-6 shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2 text-[15px] min-[1000px]:text-[16px] leading-[130%] tracking-[20%] font-tomorrow-light">
				{shouldShowAlive && (
					<span className="flex items-center gap-1.5" style={{ color: activeColor }}>
						<span className="w-4 h-4 shrink-0">
							{shouldShowStatus && (stateKey === "critical" || stateKey === "termination") && stateObj && (
								<img
									src="/images/Exclaimation.svg"
									alt="critical"
									className="w-4 h-4"
									style={{ filter: stateObj.color ? `drop-shadow(0 0 2px ${stateObj.color})` : undefined }}
								/>
							)}
							{shouldShowStatus && stateKey === "warning" && stateObj && (
								<img
									src="/images/Bell.svg"
									alt="warning"
									className="w-4 h-4"
									style={{ filter: stateObj.color ? `drop-shadow(0 0 2px ${stateObj.color})` : undefined }}
								/>
							)}
							{isDefaultState && <img src="/images/Clock.svg" alt="clock" className="w-4 h-4 shrink-0" />}
						</span>
						<span className="whitespace-nowrap">
							Active:{" "}
							<strong className="inline-block text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
								{formatSecondsAsDuration(roomState?.activeElapsedSeconds)}
							</strong>
						</span>
					</span>
				)}
				<span className="flex items-center gap-1.5 text-[#DCDCDC] whitespace-nowrap">
					<img src="/images/User.svg" alt="members" className="w-4 h-4" />
					<span>
						Members:{" "}
						<strong className="w-7 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
							{room.userCount ?? 0}
						</strong>
					</span>
				</span>
				<img src="/images/left-dirrect-arrow.svg" alt="enter room" className="w-6 h-6 min-[1000px]:ml-4 ml-auto" />
			</div>
		</button>
	);
}

export function MyRooms() {
	const navigate = useNavigate();
	const userId = useUserId();
	const [rooms, setRooms] = useState<RoomSummary[]>([]);
	const [states, setStates] = useState<RoomState[]>([]);
	const [search, setSearch] = useState("");
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [createRoomName, setCreateRoomName] = useState("");
	const [isCreatingRoom, setIsCreatingRoom] = useState(false);
	const [createRoomError, setCreateRoomError] = useState<string | null>(null);

	useEffect(() => {
		(async () => {
			const { data } = await api.chat.my.get({ query: { userId } });
			if (!data) return;
			setStates(data.states ?? []);
			setRooms((data.rooms as RoomSummary[]) ?? []);
		})();
	}, [userId]);

	const filteredRooms = rooms.filter((room) => room.name.toLowerCase().includes(search.toLowerCase().trim()));

	async function handleCreateRoom(e: React.FormEvent) {
		e.preventDefault();
		if (isCreatingRoom) return;
		setCreateRoomError(null);
		setIsCreatingRoom(true);

		const trimmedName = createRoomName.trim();
		const payload = trimmedName.length > 0 ? { userId, name: trimmedName } : { userId };
		const { data, error } = await api.chat.new.post(payload);
		setIsCreatingRoom(false);

		if (error) {
			const status = (error as any)?.status;
			const value = (error as any)?.value;
			if (status === 409) {
				setCreateRoomError("Room name already exists. Please choose another.");
			} else if (status === 429) {
				setCreateRoomError("Too many room creations. Please wait and try again.");
			} else if (status === 400 && value === "Please use a proper title!") {
				setCreateRoomError("Please use a proper title!");
			} else {
				setCreateRoomError("Failed to create room. Please try again.");
			}
			return;
		}

		if (!data) {
			setCreateRoomError("Failed to create room. Please try again.");
			return;
		}

		setIsCreateModalOpen(false);
		setCreateRoomName("");
		navigate(`/room/${data.id}`);
	}

	return (
		<div className="relative h-dvh w-full" style={{ background: "#81818130" }}>
			<div className="absolute inset-0 pointer-events-none z-0">
				<img
					src="/images/Home2.png"
					alt=""
					style={{ opacity: 0.5, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
				/>
			</div>

			<div className="absolute inset-0 z-10 p-0 min-[1000px]:p-6">
				<div className="h-full border border-white bg-black overflow-hidden flex flex-col">
					<div className="flex items-center gap-2 p-4 min-[1000px]:p-6 border-b border-white shrink-0 min-h-18 bg-[#1F1F1F] text-white">
						<button
							onClick={() => navigate("/")}
							className="group cursor-pointer hover:text-white transition-colors text-sm tracking-widest flex items-center gap-2"
						>
							<img src="/images/left-arrow.svg" alt="back" className="w-4 h-4" />
						</button>
						<span className="uppercase font-tomorrow-bold leading-[130%] tracking-[20%]">My Rooms</span>
					</div>

					<div className="p-4 min-[1000px]:p-6 border-b border-white shrink-0">
						<div className="flex items-center gap-3 border border-white bg-[#1F1F1F] px-4 min-[1000px]:px-6 py-3 min-[1000px]:py-4">
							<img src="/images/Zoom.svg" alt="search" className="w-6 h-6" />
							<input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="search..."
								className="w-full bg-transparent outline-none text-white placeholder:text-white/40 font-tomorrow-light text-[16px] leading-[130%] tracking-[20%]"
							/>
						</div>
					</div>

					<div className="flex-1 min-h-0 overflow-y-auto chat-scrollbar pl-6">
						{filteredRooms.length === 0 && (
							<p className="text-center text-white/40 text-sm tracking-[20%] py-8 uppercase font-tomorrow-light">
								No rooms created by you yet
							</p>
						)}
						{filteredRooms.map((room) => (
							<RoomRow key={room.id} room={room} states={states} />
						))}
					</div>

					<div className="shrink-0 border-t border-white py-3 px-4 bg-[#1F1F1F] flex items-center gap-3">
						<button
							onClick={() => {
								setCreateRoomError(null);
								setCreateRoomName("");
								setIsCreateModalOpen(true);
							}}
							className="w-full min-[1000px]:w-auto py-2.5 px-5 bg-black border border-white text-white leading-6 cursor-pointer tracking-[20%]"
						>
							Add Room
						</button>
					</div>
				</div>
			</div>

			{isCreateModalOpen && (
				<div
					className="absolute inset-0 z-30 bg-black/70 flex items-center justify-center p-4"
					onClick={() => setIsCreateModalOpen(false)}
				>
					<div className="w-full max-w-137.5 border border-white bg-black" onClick={(e) => e.stopPropagation()}>
						<div className="h-16 px-7 border-b border-white bg-[#1F1F1F] text-white flex items-center justify-between">
							<div className="flex items-center gap-3">
								<button
									onClick={() => setIsCreateModalOpen(false)}
									className="group cursor-pointer hover:text-white transition-colors text-sm tracking-widest flex items-center gap-2"
								>
									<img src="/images/left-arrow.svg" alt="back" className="w-4 h-4" />
								</button>
								<h2 className="uppercase font-tomorrow-bold leading-[130%] tracking-[20%]">Name The Room</h2>
							</div>
							<button
								onClick={() => setIsCreateModalOpen(false)}
								aria-label="Close"
								className="text-white/80 hover:text-white text-xl leading-none cursor-pointer"
							>
								X
							</button>
						</div>

						<form
							onSubmit={handleCreateRoom}
							className="min-h-65 px-6 min-[1000px]:px-10 py-10 min-[1000px]:py-16 flex items-center justify-center"
						>
							<div className="w-full max-w-92.5 flex flex-col gap-3 min-[1000px]:gap-5">
								<div className="w-full flex flex-col min-[640px]:flex-row items-stretch min-[640px]:items-center gap-3 min-[1000px]:gap-5">
									<input
										type="text"
										value={createRoomName}
										onChange={(e) => setCreateRoomName(e.target.value)}
										placeholder="Name..."
										className="flex-1 h-10 py-2 px-5 bg-[#1F1F1F] border border-white/60 outline-none text-white placeholder:text-white/60 font-tomorrow-light tracking-[20%]"
									/>
									<button
										type="submit"
										disabled={isCreatingRoom}
										className="w-full min-[640px]:w-auto h-10 px-7 bg-white text-black border border-white leading-6 tracking-[20%] font-tomorrow-light cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{isCreatingRoom ? "Creating..." : "Create"}
									</button>
								</div>
								{createRoomError && <p className="text-[11px] tracking-wide text-[#f5b942]">{createRoomError}</p>}
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}
